import type { FC, ReactNode } from 'react';
import { Link as LinkDoRouter } from 'react-router-dom';
import {
  Button, Form, InlineLoading, InlineNotification, Link, PasswordInput, Stack, TextInput,
} from '@carbon/react';
import { ArrowRight } from '@carbon/icons-react';

import { PaginaDeAuth } from '../../../padroes/PaginaDeAuth';
import { EmailEscolhido } from '../EmailEscolhido';
import { EntrarComProvedor, type PropsDosProvedores } from '../EntrarComProvedor';
import { ROTULOS, rotuloDoAviso } from '../../../padroes/rotulos';
import styles from '../auth.module.scss';

// ENTRAR — o padrão de login do Carbon, em dois passos: primeiro o e-mail e o "Continuar" (com as
// outras formas de entrar à vista logo embaixo); depois o e-mail escolhido com o "Trocar", a senha
// e o "Entrar". Quem ainda não confirmou o e-mail cai no passo do código, na mesma página.

export interface PropsDeEntrar {
  etapa: 'email' | 'senha';
  continuar: () => void;
  trocarEmail: () => void;
  email: string;
  setEmail: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  loading: boolean;
  error: string | null;
  erroDoEmail?: string;
  erroDaSenha?: string;
  validarEmail: () => void;
  validarSenha: () => void;
  needsVerify: boolean;
  entrar: () => void;
  esqueci: () => void;
  linkDoCadastro: string;
  /** O passo do código, para quem ainda não confirmou o e-mail. */
  codigo: ReactNode;
  provedores: PropsDosProvedores;
}

export const Entrar: FC<PropsDeEntrar> = ({
  etapa, continuar, trocarEmail, email, setEmail, password, setPassword, loading, error, erroDoEmail, erroDaSenha, validarSenha,
  needsVerify, entrar, esqueci, linkDoCadastro, codigo, provedores,
}) => {
  if (needsVerify) {
    return (
      <PaginaDeAuth titulo='Confirme seu e-mail'>
        {codigo}
        <EntrarComProvedor {...provedores} />
      </PaginaDeAuth>
    );
  }

  return (
    <PaginaDeAuth
      titulo='Entrar na Maestra'
      apoio={<>Não tem conta? <Link as={LinkDoRouter} to={linkDoCadastro}>Cadastre-se</Link></>}
    >
      <Form noValidate onSubmit={(e) => { e.preventDefault(); if (etapa === 'email') continuar(); else entrar(); }}>
        {etapa === 'email' ? (
          <Stack gap={6}>
            <TextInput
              id='entrar-email'
              type='email'
              labelText='E-mail'
              autoComplete='email'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              invalid={!!erroDoEmail}
              invalidText={erroDoEmail}
              autoFocus
            />
            <div className={styles.acoes}>
              <Button type='submit' renderIcon={ArrowRight}>Continuar</Button>
            </div>
          </Stack>
        ) : (
        <Stack gap={6}>
          <EmailEscolhido rotulo='Entrando como' email={email.trim()} trocar={trocarEmail} />
          <PasswordInput
            id='entrar-senha'
            labelText='Senha'
            autoComplete='current-password'
            showPasswordLabel='Mostrar senha'
            hidePasswordLabel='Ocultar senha'
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onBlur={validarSenha}
            invalid={!!erroDaSenha}
            invalidText={erroDaSenha}
            autoFocus
          />
          {/* Chegar aqui já significa que o servidor descartou "falta confirmar": sobram senha
              errada e conta do Google, e a do Google é a mais provável. */}
          {error && (
            <InlineNotification
              kind='error'
              lowContrast
              hideCloseButton
              title={error}
              subtitle='Criou a conta com o Google? Entre pelo Google, logo abaixo.'
              statusIconDescription={rotuloDoAviso('error')}
              aria-label={ROTULOS.fecharAviso}
            />
          )}
          <div className={styles.acoes}>
            {loading
              ? <InlineLoading description='Entrando…' iconDescription={ROTULOS.carregando} />
              : <Button type='submit' renderIcon={ArrowRight}>Entrar</Button>}
            <Link href='/esqueci-senha' onClick={(e: React.MouseEvent) => { e.preventDefault(); esqueci(); }}>
              Esqueceu a senha?
            </Link>
          </div>
        </Stack>
        )}
      </Form>
      <EntrarComProvedor {...provedores} />
    </PaginaDeAuth>
  );
};
